import { IRootScopeService, IPromise, IScope, IQService } from "angular";
import { StateService } from "@uirouter/angularjs";
import { IModalServiceInstance, IModalScope, IModalService } from "angular-ui-bootstrap";
import { ListTypeKeysUI, IList, ListTypes, DEFAULT_LIST_COLOR } from "../models/models";
import { ListService } from "../services/listService";
import { CurrentUserService } from "@gtmhub/users/currentUserService";
import { PermissionsService } from "@gtmhub/users/permissionsService";
import { IUIError, UIErrorHandlingService } from "@gtmhub/error-handling";
import { IUserItemAllowedActions } from "@gtmhub/sessions/models/models";
import { AnalyticsService } from "@gtmhub/core/analytics";
import { IStateInit } from "@gtmhub/core/routing";
import { IAssigneeIdMap } from "@gtmhub/assignees/models";
import { IRestLayerRequest } from "@gtmhub/core";
import { AssigneeActions, IAssigneesStoreState } from "@gtmhub/assignees";
import { INgRedux } from "@gtmhub/state-management";
import {
  GridFilterDefinitions,
  hasFilterValue,
  IGridFilter,
  toRestLayerRequest,
  IGridFiltersChange,
  getPersistedScreenSettings,
  setPersistedScreenSettings,
} from "@gtmhub/shared/components/grid-filtering-bar";
import { generateListsGridFilters, listsGridFilterDefinitions } from "../filter-utils";
import { IExtendedRootScopeService } from "@gtmhub/shared/ng-extensions";
import { getCurrentUserRoles } from "@gtmhub/users";
import { userHasOnlyResctrictedRole } from "./helpers";
import { FavoritesManager } from "@gtmhub/favorites/favorites.manager";
import { INavItem } from "@webapp/navigation/models/nav-items-list.models";
import { navItemsFactory } from "@webapp/navigation/utils/nav-items.util";
import { AccountService } from "@gtmhub/accounts/accountsService";
import { FeatureToggleV2Service } from "@gtmhub/featureToggle";
import { FeatureFlag } from "@gtmhub/featureToggle/models";
import { FavoritesManagerFactory } from "@gtmhub/favorites/favorites.manager.factory";

interface IConfirmDeleteScope extends IModalScope {
  confirm(): void;
}

export interface IListsScope extends IScope, IExtendedRootScopeService, IScopeFromRedux {
  indicators: {
    initialLoad: boolean;
  };
  reloadingList: boolean;
  listItems: IList[];
  listTypesUI: typeof ListTypeKeysUI;
  allowedActions: { [listId: string]: string[] };
  propertiesDictionary: { [field: string]: string };
  navListsIds: string[];
  currentPage: number;
  totalPages: number;
  currentUserRestricted: boolean;
  changeListStateInNavigation(list: IList): void;
  checkPermissions(listId: string): void;
  confirmDelete(list: IList): void;
  cloneList(listId: string): void;
  onPageChange(page: number): void;
  createList(): void;
  loadPage(page: number): void;
  filterDefinitions: GridFilterDefinitions;
  filters: IGridFilter[];
  filtersChange(change: IGridFiltersChange): void;
  anyFiltersApplied: boolean;
  favoritesManager: FavoritesManager;
  getNavItem(list: IList): INavItem;
  listIsFavorite(list: IList): boolean;
  isNewExperienceEnabled(): boolean;
}

export enum ListChangedEvent {
  Added = "ADDED",
  Deleted = "DELETED",
  Modified = "MODIFIED",
}
interface IScopeFromRedux {
  reduxStatesInitialized: boolean;
  reduxStateError: IUIError;
  assigneesIdMap: IAssigneeIdMap;
}

const bindStateToScope = (state: IAssigneesStoreState): IScopeFromRedux => {
  const { map: assigneesIdMap, isFetched: assigneesFetched, error: assigneesError } = state.assignees;

  return {
    reduxStatesInitialized: assigneesFetched,
    reduxStateError: assigneesError,
    assigneesIdMap,
  };
};

const screenPath = ["lists"];

export class ListsCtrl implements IStateInit {
  private favoritesManager: FavoritesManager;

  public static $inject = [
    "$scope",
    "$uibModal",
    "$state",
    "ListService",
    "CurrentUserService",
    "$rootScope",
    "PermissionsService",
    "UIErrorHandlingService",
    "AnalyticsService",
    "AssigneeActions",
    "FavoritesManagerFactory",
    "FeatureToggleV2Service",
    "AccountService",
    "$q",
    "$ngRedux",
  ];

  constructor(
    private $scope: IListsScope,
    private $uibModal: IModalService,
    private $state: StateService,
    private listService: ListService,
    private currentUserService: CurrentUserService,
    private $rootScope: IRootScopeService,
    private permissionsService: PermissionsService,
    private uiErrorHandlingService: UIErrorHandlingService,
    private analyticsService: AnalyticsService,
    private assigneeActions: AssigneeActions,
    private favoritesManagerFactory: FavoritesManagerFactory,
    private featureToggleService: FeatureToggleV2Service,
    private accountService: AccountService,
    private $q: IQService,
    private $ngRedux: INgRedux
  ) {
    const unsubscribe = this.$ngRedux.connect(bindStateToScope)(this.$scope);
    this.$scope.$on("$destroy", unsubscribe);
    this.favoritesManager = this.favoritesManagerFactory(["list"]);
  }

  public stateInit(): IPromise<unknown> {
    this.$ngRedux.dispatch(this.assigneeActions.getAssignees());

    this.initGridFilteringBar();

    return this.$scope
      .watchUntilAndRejectOnError(
        () => this.$scope.reduxStatesInitialized,
        () => this.$scope.reduxStateError
      )
      .then(
        () => this.init(),
        () => this.$scope.reduxStateError
      )
      .catch((err) => this.uiErrorHandlingService.handleModal(err));
  }

  private pagingSize = 100;
  private confirmDeleteModal: IModalServiceInstance;
  private checkingEnabledFeatures: boolean;
  private showNewExperience: boolean;

  private init = (): void => {
    this.$scope.indicators = {
      initialLoad: true,
    };

    this.$scope.listTypesUI = ListTypeKeysUI;
    this.$scope.allowedActions = {};
    this.$scope.propertiesDictionary = this.listService.getListPropertiesDictionary();

    this.$scope.currentUserRestricted = userHasOnlyResctrictedRole(getCurrentUserRoles());

    this.$scope.navListsIds = this.currentUserService.getUserSetting("listIds");
    this.$scope.createList = this.createList;
    this.$scope.changeListStateInNavigation = this.changeListStateInNavigation;
    this.$scope.onPageChange = this.onPageChange;
    this.$scope.checkPermissions = this.checkPermissions;
    this.$scope.confirmDelete = this.confirmDelete;
    this.$scope.cloneList = this.cloneList;
    this.$scope.favoritesManager = this.favoritesManager;
    this.$scope.getNavItem = this.getNavItem;
    this.$scope.listIsFavorite = this.listIsFavorite;
    this.$scope.isNewExperienceEnabled = this.isNewExperienceEnabled;

    this.$scope.currentPage = 1;

    this.checkingEnabledFeatures = true;
    this.showNewExperience = false;
    const promises = {
      newExperienceEnabled: this.featureToggleService.isFeatureAvailable(FeatureFlag.NewExperience),
      newExperienceToggleEnabled: this.featureToggleService.isFeatureAvailable(FeatureFlag.NewExperienceToggle),
    };

    this.$q
      .all(promises)
      .then(({ newExperienceEnabled, newExperienceToggleEnabled }) => {
        const newExperienceAccountSettingEnabled = this.accountService.getAccountSetting<Record<"enabled", boolean>>("newExperience")?.enabled;
        this.showNewExperience = newExperienceEnabled && newExperienceToggleEnabled && newExperienceAccountSettingEnabled;
        this.checkingEnabledFeatures = false;
        if (this.showNewExperience) {
          this.favoritesManager.initFavorites();
        }
      })
      .catch(() => (this.checkingEnabledFeatures = false));
  };

  private initGridFilteringBar = () => {
    this.$scope.filterDefinitions = listsGridFilterDefinitions();
    this.$scope.filters = getPersistedScreenSettings(screenPath, "filters") || generateListsGridFilters(this.$scope.filterDefinitions);
    this.$scope.filtersChange = this.filtersChange;
  };

  private filtersChange = (change: IGridFiltersChange): void => {
    if (!change.initial) {
      setPersistedScreenSettings(screenPath, "filters", this.$scope.filters);
    }

    this.$scope.anyFiltersApplied = this.$scope.filters.some((f) => hasFilterValue(f));
    this.$scope.reloadingList = true;
    this.$scope.currentPage = 1;
    this.getLists();
  };

  private createListsRequest = (): IRestLayerRequest => {
    const request = toRestLayerRequest({ filters: this.$scope.filters });
    request.skip = (this.$scope.currentPage - 1) * this.pagingSize;
    request.limit = this.pagingSize;
    return request;
  };

  private onPageChange = (page: number): void => {
    this.$scope.currentPage = page;
    this.$scope.reloadingList = true;
    this.getLists();
  };

  private getLists = (): IPromise<void> => {
    const request: IRestLayerRequest = this.createListsRequest();

    return this.listService.getLists(request).then(
      (listsCollection) => {
        this.$scope.reloadingList = false;
        this.$scope.indicators.initialLoad = false;
        this.$scope.listItems = listsCollection.items.map((list) => ((list.uiAccess = this.listService.getListAccessType(list)), list));
        this.$scope.totalPages = Math.ceil(listsCollection.totalCount / this.pagingSize);
      },
      () => {
        this.$scope.reloadingList = false;
        this.$scope.indicators.initialLoad = false;
      }
    );
  };

  private createList = (): void => {
    const createList: Partial<IList> = {
      color: DEFAULT_LIST_COLOR,
      type: ListTypes.Objective,
    };

    this.listService.createList(createList).then(
      (newList) => {
        this.addNewListIdToUserSetting(newList.id);

        this.$rootScope.$broadcast("listCreated", newList, ListChangedEvent.Added);
        this.$state.go("gtmhub.list", { listId: newList.id, isNewList: true });
      },
      (error) => {
        this.uiErrorHandlingService.handleModal(error);
      }
    );
  };

  private addNewListIdToUserSetting(id: string): void {
    let listsIds = this.currentUserService.getUserSetting("listIds");
    listsIds = !listsIds ? [id] : [...listsIds, id];

    this.currentUserService.setUserSetting({ listIds: listsIds });
  }

  private changeListStateInNavigation = (list: IList) => {
    let listChangedEvent: ListChangedEvent;

    if (!this.$scope.navListsIds) {
      this.$scope.navListsIds = [];
    }

    if (this.$scope.navListsIds.includes(list.id)) {
      this.$scope.navListsIds = this.$scope.navListsIds.filter((id) => id !== list.id);
      listChangedEvent = ListChangedEvent.Deleted;

      this.analyticsService.track("List Deleted From Navigation");
    } else {
      this.$scope.navListsIds = [...this.$scope.navListsIds, list.id];
      listChangedEvent = ListChangedEvent.Added;
      this.analyticsService.track("List Added To Navigation");
    }

    this.currentUserService.setUserSetting({ listIds: this.$scope.navListsIds });
    this.$rootScope.$broadcast("listUpdated", list, listChangedEvent);
  };

  private checkPermissions = (listId: string): void => {
    this.permissionsService.getCurrentUserAllowedActionsPerItem(listId, "list").then(
      (actions: IUserItemAllowedActions) => {
        this.$scope.allowedActions[listId] = actions.currentUserActions;
      },
      () => {
        // If the request fails what will happen is that the user won't see the delete button
        // it's better to stay without indicator
      }
    );
  };

  private deleteList = (list: IList): void => {
    this.listService.deleteList({ id: list.id }).then(
      () => {
        if (this.$scope.navListsIds.includes(list.id)) {
          this.$scope.navListsIds = this.$scope.navListsIds.filter((id) => id !== list.id);
          this.currentUserService.setUserSetting({ listIds: this.$scope.navListsIds });
          this.$rootScope.$broadcast("listDeleted", list, ListChangedEvent.Deleted);
        }
        this.confirmDeleteModal.close();
        this.$scope.listItems = this.$scope.listItems.filter((listItem) => listItem.id !== list.id);
      },
      (error) => this.uiErrorHandlingService.handleModal(error)
    );
  };

  private confirmDelete = (list: IList): void => {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const ctrl = this;
    this.confirmDeleteModal = this.$uibModal.open({
      templateUrl: require("../views/confirm-delete.jade"),
      windowClass: "medium-small",
      controller: [
        "$scope",
        function ($scope: IConfirmDeleteScope) {
          $scope.confirm = () => {
            ctrl.deleteList(list);
          };
        },
      ],
    });
  };

  private cloneList = (listId: string): void => {
    this.listService.cloneList({ id: listId }).then(
      (clonedList) => {
        this.$state.go("gtmhub.list", { listId: clonedList.id, isNewList: true });

        this.$scope.listItems = [...this.$scope.listItems, clonedList];
      },
      (error) => this.uiErrorHandlingService.handleModal(error)
    );
  };

  private getNavItem = (list: IList): INavItem => {
    return this.favoritesManager.getFavoriteItem({ id: list.id }) || navItemsFactory.buildListNavItem(list);
  };

  private listIsFavorite = (list: IList): boolean => {
    if (!this.isNewExperienceEnabled()) return false;
    return this.favoritesManager.entityIsFavorite({ id: list.id });
  };

  public isNewExperienceEnabled = (): boolean => {
    return !this.checkingEnabledFeatures && this.showNewExperience;
  };
}
